import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { MapPin, Phone, CheckCircle2, ArrowRight, Clock, Shield, Star, Home, Building2, Landmark } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import BackButton from '../components/BackButton';
import { findLandingPage, getLocalLandingData } from '../data/localLandingData';
import { companyInfo } from '../mock';

const LocalLanding = () => {
  const { slug } = useParams();
  const data = findLandingPage(slug);
  const [cityContent, setCityContent] = useState(null);

  useEffect(() => {
    if (!data) return;
    fetch('/data/villes-content.json')
      .then(r => r.json())
      .then(json => setCityContent(json[data.city.slug] || null))
      .catch(() => {});
  }, [data]);

  if (!data) return <Navigate to="/" replace />;

  const { service, city } = data;
  const title = `${service.title} à ${city.name} (${city.cp}) | ${companyInfo.name}`;
  const description = cityContent?.intro_unique
    ? cityContent.intro_unique.substring(0, 155) + '...'
    : `Expert en ${service.title.toLowerCase()} à ${city.name}, ${city.dept}. Devis gratuit, intervention rapide dans un rayon de 65 km.`;

  const otherCitiesForService = getLocalLandingData()
    .filter(p => p.service.id === service.id && p.city.slug !== city.slug)
    .slice(0, 8);

  const otherServicesForCity = getLocalLandingData()
    .filter(p => p.city.slug === city.slug && p.service.id !== service.id);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO title={title} description={description} url={`https://duma-expert-beton.fr/${data.slug}`} />

      {/* Hero */}
      <section className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-5xl">
          <BackButton />
          <div className="mt-6">
            <div className="flex items-center gap-2 text-amber-400 text-sm font-medium mb-3">
              <MapPin className="w-4 h-4" />
              {city.name}, {city.dept}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" data-testid="landing-h1">
              {service.title} à {city.name}
            </h1>
            <p className="text-lg text-slate-300 mt-4 max-w-3xl leading-relaxed">
              {companyInfo.name}, votre expert en {service.title.toLowerCase()} à {city.name} et ses environs.
              Profitez de notre savoir-faire de plus de 11 ans pour vos projets de béton décoratif.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/devis">
                <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6" data-testid="landing-cta-devis">
                  Devis Gratuit <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/rdv">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6" data-testid="landing-cta-rdv">
                  Prendre Rendez-vous
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phoneRaw}`}>
                <Button variant="outline" className="border-amber-400 text-amber-400 hover:bg-amber-400/10 px-6">
                  <Phone className="w-4 h-4 mr-2" /> {companyInfo.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12">

        {/* Unique city context - AI generated */}
        {cityContent && (
          <section className="mb-12" data-testid="city-unique-content">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Le béton décoratif à {city.name}
            </h2>
            <p className="text-slate-700 leading-relaxed text-base mb-6">
              {cityContent.intro_unique}
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Landmark className="w-5 h-5 text-amber-500" />
                  <h3 className="font-bold text-slate-900">Projets typiques à {city.name}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {cityContent.projets_typiques}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-blue-500" />
                  <h3 className="font-bold text-slate-900">Types de propriétés</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {cityContent.types_proprietes}
                </p>
              </div>
            </div>

            {cityContent.particularites_climat && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                  <Home className="w-5 h-5 text-amber-600" />
                  Climat et béton décoratif à {city.name}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {cityContent.particularites_climat}
                </p>
              </div>
            )}
          </section>
        )}

        {/* Service Description */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            {service.title} à {city.name} — Notre Expertise
          </h2>
          <p className="text-slate-700 leading-relaxed text-base">
            {service.description}
          </p>
        </section>

        {/* Key benefits */}
        <section className="mb-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {service.features.map((feature, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="pt-5 pb-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700 font-medium">{feature}</span>
              </CardContent>
            </Card>
          ))}
        </section>

        {/* Why choose us */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            Pourquoi choisir {companyInfo.name} à {city.name} ?
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <Star className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-2">+2000 Projets</h3>
                <p className="text-sm text-slate-600">Plus de 2000 réalisations depuis 2014 dans toute la région PACA</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Clock className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-2">Intervention Rapide</h3>
                <p className="text-sm text-slate-600">
                  {city.distance === 0
                    ? `Basés à ${city.name}, intervention immédiate`
                    : `À seulement ${city.distance} km de notre base`}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <Shield className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-bold text-slate-900 mb-2">Garantie Décennale</h3>
                <p className="text-sm text-slate-600">Assurance décennale et responsabilité civile professionnelle</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Applications */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Applications du {service.title} à {city.name}
          </h2>
          <div className="flex flex-wrap gap-3">
            {service.applications.map((app, i) => (
              <span key={i} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">
                {app}
              </span>
            ))}
          </div>
        </section>

        {/* Other services in this city */}
        {otherServicesForCity.length > 0 && (
          <section className="mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              Autres services à {city.name}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {otherServicesForCity.map(page => (
                <Link
                  key={page.slug}
                  to={`/${page.slug}`}
                  className="px-4 py-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium transition-colors text-center"
                  data-testid={`service-link-${page.slug}`}
                >
                  {page.service.title}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mb-12">
          <Card className="bg-slate-900 text-white border-0">
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-3">
                Votre projet de {service.title.toLowerCase()} à {city.name}
              </h2>
              <p className="text-slate-300 mb-6 max-w-xl mx-auto">
                Obtenez un devis gratuit et personnalisé pour votre projet. Notre équipe se déplace à {city.name} et dans tout le département {city.dept}.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link to="/devis">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8">
                    Devis Gratuit en Ligne
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                    Nous Contacter
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Other cities for same service */}
        {otherCitiesForService.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              {service.title} dans d'autres villes
            </h2>
            <div className="flex flex-wrap gap-2">
              {otherCitiesForService.map(page => (
                <Link
                  key={page.slug}
                  to={`/${page.slug}`}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors"
                  data-testid={`internal-link-${page.slug}`}
                >
                  {service.title} à {page.city.name}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default LocalLanding;
